import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MonthDayPicker from '../../components/common/MonthDayPicker/MonthDayPicker';
import CountrySelectWithFlag from '../../components/common/SelectBox/CountrySelectWithFlag';
import ErrorNotification from '../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../components/common/AlertBox/GreenAlert';
import { fetchActiveCountries } from '../../services/Localization/CountryService';
import { fetchFinancialYearById, updateFinancialYear } from '../../services/Localization/FinancialYearService';
import '../../components/common/CommonText.css';
import './FinancialYear.css';

const FinancialYearEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const createDateFromMonthDay = (month, day) => {
    if (!month || !day) return null;
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, month - 1, day);
  };

  useEffect(() => {
    const initData = async () => {
      try {
        const countries = await fetchActiveCountries();
        setCountryOptions(countries || []);

        const res = await fetchFinancialYearById(id);
        if (res && res.response_code === 1 && res.data) {
          const item = res.data;
          if (item.start_month && item.start_day) {
            setStartDate(createDateFromMonthDay(item.start_month, item.start_day));
          }
          if (item.end_month && item.end_day) {
            setEndDate(createDateFromMonthDay(item.end_month, item.end_day));
          }
          if (item.countries && Array.isArray(item.countries)) {
            setSelectedCountries(item.countries.map(c => ({
              label: c.name,
              value: c.id,
              country_flag: c.country_flag
            })));
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setErrors({ general: "Failed to load financial year data." });
      } finally {
        setLoading(false);
      }
    };

    if (id) initData();
  }, [id]);

  const handleCountrySelect = (e) => {
    const countryId = Number(e.target.value);
    if (!countryId) return;
    const selectedObj = countryOptions.find(c => Number(c.value) === countryId);
    if (selectedObj && !selectedCountries.some(c => Number(c.value) === countryId)) {
      setSelectedCountries(prev => [...prev, selectedObj]);
    }
  };

  const removeCountry = (countryId) => {
    setSelectedCountries(prev => prev.filter(c => Number(c.value) !== Number(countryId)));
  };

  const formatDateString = (dateObj) => {
    if (!dateObj) return "";
    return dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});

    if (!startDate) {
      setErrors({ startDate: "Start Date is required" });
      return;
    }
    if (!endDate) {
      setErrors({ endDate: "End Date is required" });
      return;
    }

    const startMonth = startDate.getMonth() + 1;
    const startDay = startDate.getDate();
    const endMonth = endDate.getMonth() + 1;
    const endDay = endDate.getDate();
    const startStr = formatDateString(startDate);
    const endStr = formatDateString(endDate);

    const payload = {
      name: `${startStr} - ${endStr}`,
      start_month: startMonth,
      start_day: startDay,
      end_month: endMonth,
      end_day: endDay,
      country_ids: selectedCountries.map(c => Number(c.value))
    };

    setSubmitting(true);
    try {
      const res = await updateFinancialYear(id, payload);
      if (res && res.response_code === 1) {
        SuccessAlert("Financial Year updated successfully");
        navigate(`/financial-year/detail/${id}`);
      } else {
        if (typeof res?.message === 'string') {
          setErrors({ general: res.message });
        } else if (res?.message && typeof res.message === 'object') {
          setErrors(res.message);
        } else {
          setErrors({ general: "Failed to update financial year" });
        }
      }
    } catch (err) {
      console.error(err);
      setErrors({ general: "An error occurred while updating." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="whole-container">
        <p className="common-text fts-14 ftw-4" style={{ padding: '20px' }}>Loading...</p>
      </div>
    );
  }

  const availableCountryOptions = countryOptions.filter(
    (c) => !selectedCountries.some((sc) => Number(sc.value) === Number(c.value))
  );

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization /"
          nestedpageName="Edit Financial Year"
          linkTo="/financial-year"
        />
      </div>

      <div className="fy-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        <div className="fy-card">
          <div className="fy-card-header">
            <h2 className="fy-card-title">Edit Financial Year</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="fy-form-group">
              <MonthDayPicker
                label="Start Date"
                selectedDate={startDate}
                onChange={(date) => setStartDate(date)}
                isRequired
                error={errors.startDate}
              />
            </div>

            <div className="fy-form-group">
              <MonthDayPicker
                label="End Date"
                selectedDate={endDate}
                onChange={(date) => setEndDate(date)}
                isRequired
                error={errors.endDate}
              />
            </div>

            <div className="fy-section-title">Select Country</div>

            <div className="fy-form-group">
              <CountrySelectWithFlag
                label="Country"
                options={availableCountryOptions}
                onChange={handleCountrySelect}
                placeholder="Select Country"
              />
            </div>

            {selectedCountries.length > 0 && (
              <div className="fy-country-pills-container">
                {selectedCountries.map((country) => (
                  <div key={country.value} className="fy-country-pill">
                    <span>{country.label}</span>
                    <span
                      className="remove-icon"
                      onClick={() => removeCountry(country.value)}
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="fy-actions">
              <button
                type="submit"
                className="fy-submit-btn"
                disabled={submitting}
              >
                {submitting ? "Updating..." : "Update Financial Year"}
              </button>
              <button
                type="button"
                className="fy-cancel-btn"
                onClick={() => navigate('/financial-year')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FinancialYearEdit;
